import React, { useState, useEffect } from "react";
import { getWorker, getLocations, verifyMfa, requestMfaEmailCode, logoutWorker, UNAUTHORIZED_EVENT } from "./api";
import { Worker, Location, MfaMethod, LoginResponse } from "./types";
import { useLang } from "./LangContext";
import { isNetworkError } from "./detailMessage";
import ServiceUnavailablePage from "./components/ServiceUnavailablePage";
import useTheme from "./useTheme";
import MfaSetupPage from "./MfaSetupPage";
import AvailabilityManager from "./AvailabilityManager";
import BookingsOverview from "./BookingsOverview";
import LoginPage from "./auth/LoginPage";
import MfaChallenge from "./auth/MfaChallenge";
import DashboardTopbar from "./dashboard/DashboardTopbar";
import SchedulingLinkCard from "./dashboard/SchedulingLinkCard";
import ProfileOverlay from "./dashboard/ProfileOverlay";
import AboutOverlay from "./dashboard/AboutOverlay";
import ErrorBoundary from "./components/ErrorBoundary";
import ApiErrorBoundary from "./components/ApiErrorBoundary";
import useAppointmentEvents from "./useAppointmentEvents";
import { useNavigate } from "react-router-dom";
import "./App.css";

export default function App() {
  const navigate = useNavigate();
  const { setLangFromBackend, setAuthToken } = useLang();
  useTheme();
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [worker, setWorker] = useState<Worker | null>(null);
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaMethods, setMfaMethods] = useState<MfaMethod[]>([]);
  const [mfaActiveMethod, setMfaActiveMethod] = useState<MfaMethod>("totp");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaError, setMfaError] = useState("");
  const [mfaSubmitting, setMfaSubmitting] = useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [showProfileManager, setShowProfileManager] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [shareDuration, setShareDuration] = useState("");
  const [shareLocationId, setShareLocationId] = useState("");
  const [locations, setLocations] = useState<Location[]>([]);
  const [appointmentRefreshTick, setAppointmentRefreshTick] = useState(0);
  const [backendDown, setBackendDown] = useState(false);

  useAppointmentEvents(worker?.public_id, () =>
    setAppointmentRefreshTick((n) => n + 1)
  );

  useEffect(() => {
    setAuthToken(token);
  }, [token]);

  useEffect(() => {
    if (token) {
      getWorker(token)
        .then((workerData) => {
          setWorker(workerData);
          if (typeof workerData.default_duration === "number") {
            setShareDuration(String(workerData.default_duration));
          }
          if (!workerData.mfa_email_enabled
              && !workerData.mfa_totp_enabled
              && !workerData.mfa_setup_dismissed) {
            setMfaSetupRequired(true);
          }
          if (workerData.lang === 'de' || workerData.lang === 'en') {
            setLangFromBackend(workerData.lang);
          }
        })
        .catch((err: unknown) => {
          if (isNetworkError(err)) {
            setBackendDown(true);
          } else {
            console.error("Auth error:", (err as Error).message);
            setToken("");
            setWorker(null);
            localStorage.removeItem("token");
            navigate("/");
          }
        });
    }
  }, [token, navigate]);

  const refreshLocations = (data: Location[] | null) => {
    setLocations(data || []);
    if (data && data.length) {
      setShareLocationId((prev) => {
        if (prev && data.some((l) => String(l.id) === String(prev))) return prev;
        const def = data.find((l) => l.is_default);
        return def ? String(def.id) : String(data[0].id);
      });
    } else {
      setShareLocationId("");
    }
  };

  useEffect(() => {
    const handler = () => {
      setToken("");
      setWorker(null);
      navigate("/");
    };
    window.addEventListener(UNAUTHORIZED_EVENT, handler);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handler);
  }, [navigate]);

  useEffect(() => {
    if (!token) return;
    Promise.resolve(getLocations(token))
      .then(refreshLocations)
      .catch((err) => console.error("Failed to load locations:", err));
  }, [token]);

  const handleAuthenticated = (res: LoginResponse) => {
    if (res.mfa_required === true) {
      setMfaToken(res.mfa_token);
      setMfaMethods(res.mfa_methods);
      setMfaActiveMethod(res.mfa_methods.includes("totp") ? "totp" : "email");
      setMfaPending(true);
    } else {
      setToken(res.access_token);
      localStorage.setItem("token", res.access_token);
    }
  };

  const submitMfa = async () => {
    if (mfaSubmitting || !mfaCode) return;
    setMfaSubmitting(true);
    setMfaError("");
    try {
      const res = await verifyMfa(mfaToken, mfaCode, mfaActiveMethod);
      setToken(res.access_token);
      localStorage.setItem("token", res.access_token);
      setMfaPending(false);
      setMfaCode("");
    } catch (err) {
      setMfaError((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || "Invalid code. Please try again.");
    } finally {
      setMfaSubmitting(false);
    }
  };

  const resendMfaCode = async () => {
    try {
      await requestMfaEmailCode(mfaToken);
    } catch (err) {
      setMfaError((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || "Failed to resend code.");
    }
  };

  const cancelMfa = () => {
    setMfaPending(false);
    setMfaToken("");
    setMfaMethods([]);
    setMfaActiveMethod("totp");
    setMfaCode("");
    setMfaError("");
  };

  const switchMfaMethod = (method: MfaMethod) => {
    setMfaActiveMethod(method);
    setMfaCode("");
    setMfaError("");
    if (method === "email") {
      requestMfaEmailCode(mfaToken).catch(() => {});
    }
  };

  const logout = () => {
    logoutWorker();
    setToken("");
    setWorker(null);
    localStorage.removeItem("token");
  };

  if (backendDown) {
    return <ServiceUnavailablePage onRetry={() => { setBackendDown(false); window.location.reload(); }} />;
  }

  if (!token) {
    if (mfaPending) {
      return (
        <MfaChallenge
          methods={mfaMethods}
          activeMethod={mfaActiveMethod}
          code={mfaCode}
          error={mfaError}
          submitting={mfaSubmitting}
          onCodeChange={setMfaCode}
          onSubmit={submitMfa}
          onCancel={cancelMfa}
          onResend={resendMfaCode}
          onSwitchMethod={switchMfaMethod}
        />
      );
    }

    return <LoginPage onAuthenticated={handleAuthenticated} />;
  }

  if (token && worker && mfaSetupRequired) {
    return (
      <MfaSetupPage
        token={token}
        onComplete={async () => {
          const updated = await getWorker(token);
          setWorker(updated);
          setMfaSetupRequired(false);
        }}
        onSkip={() => {
          setWorker((prev) => prev ? { ...prev, mfa_setup_dismissed: true } : prev);
          setMfaSetupRequired(false);
        }}
      />
    );
  }

  return (
    <ErrorBoundary>
      <main className="dashboard-container">
        <DashboardTopbar
          workerFirstName={worker?.first_name}
          workerCustomPublicId={worker?.custom_public_id}
          workerLogoUrl={worker?.logo_url}
          onOpenSettings={() => setShowProfileManager(true)}
          onOpenAbout={() => setShowAbout(true)}
          onLogout={logout}
        />

        <div className="dashboard-view">
          <div className="dashboard-left card">
            <ApiErrorBoundary>
              <BookingsOverview
                token={token}
                refreshTick={appointmentRefreshTick}
                locations={locations}
              />
            </ApiErrorBoundary>
          </div>

          <div className="dashboard-right">
            <SchedulingLinkCard
              worker={worker}
              locations={locations}
              shareDuration={shareDuration}
              shareLocationId={shareLocationId}
              onShareDurationChange={setShareDuration}
              onShareLocationIdChange={setShareLocationId}
            />
            <div className="card">
              <ApiErrorBoundary>
                <AvailabilityManager token={token} />
              </ApiErrorBoundary>
            </div>
          </div>
        </div>

        {showAbout && (
          <AboutOverlay onClose={() => setShowAbout(false)} />
        )}

        {showProfileManager && (
          <ProfileOverlay
            token={token}
            onClose={() => setShowProfileManager(false)}
            onDurationOptionsChange={(opts) => {
              setWorker((prev) => (prev ? { ...prev, duration_options: opts } : prev));
              if (shareDuration && !opts.map(String).includes(shareDuration)) {
                setShareDuration("");
              }
            }}
            onDefaultDurationChange={(value) => {
              setWorker((prev) =>
                prev ? { ...prev, default_duration: value } : prev
              );
              setShareDuration(typeof value === "number" ? String(value) : "");
            }}
            onCustomPublicIdChange={(slug) => {
              setWorker((prev) =>
                prev ? { ...prev, custom_public_id: slug || null } : prev
              );
            }}
            onLocationsChange={refreshLocations}
            onLogoChange={(url) =>
              setWorker((prev) => (prev ? { ...prev, logo_url: url } : prev))
            }
          />
        )}
      </main>
    </ErrorBoundary>
  );
}
